import { Alert } from 'antd';
import { Component, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Keeps a fault in the address section inside the address section.
 *
 * **Why this exists.** On 2026-09-14 a single `TypeError` thrown while rendering the calibration
 * panel unmounted the entire React tree: not the panel, not the section, not the modal — the whole
 * application, which an operator saw as a blank white page with a real identity card still sitting
 * on the reader. There was no boundary anywhere above it, so the smallest defect in the most
 * optional part of a scan result could take down the page that runs the counter.
 *
 * **What it protects and what it deliberately does not.** It wraps the address section alone. The
 * identity fields, the validation warnings, the portrait and every control the operator needs in
 * order to finish are outside it and stay on screen, because the address is the one part of a scan
 * result that is genuinely optional — the operator can always type it. Putting the boundary any
 * higher would let it swallow a failure in something they cannot work around.
 *
 * **The fallback says what to do and nothing else.** No error message, no stack, no component name
 * and nothing from the response: the thing that threw was handling a document, and a boundary is
 * not a place to start quoting it. What the operator gets is that the section failed, that nothing
 * was filled in for them, and that they should type the address as usual.
 */
interface DocumentAddressBoundaryProps {
    /** Reported once per fault, so a render failure is not silent. Never given document content. */
    onFailure?: () => void;
    children?: ReactNode;
}

interface DocumentAddressBoundaryState {
    failed: boolean;
}

export class DocumentAddressBoundary extends Component<
    DocumentAddressBoundaryProps,
    DocumentAddressBoundaryState
> {
    constructor(props: DocumentAddressBoundaryProps) {
        super(props);

        this.state = { failed: false };
    }

    static getDerivedStateFromError(): DocumentAddressBoundaryState {
        return { failed: true };
    }

    // React passes the error and the component stack here; neither is declared, and neither may
    // ever be. `error.message` and the stack are written by whatever threw, and what threw here was
    // rendering a person's address - so the caller is told that a failure happened and is told
    // nothing about it. Whoever adds a parameter to this method is adding a place where an address
    // can leak, and must say where it goes.
    componentDidCatch(): void {
        this.props.onFailure?.();
    }

    render(): ReactNode {
        return this.state.failed ? <AddressUnavailable /> : this.props.children;
    }
}

/**
 * What replaces the section when it could not be drawn.
 *
 * A function component so the sentences come from the catalogue like every other sentence on this
 * screen; a class boundary cannot use the translation hook itself. Exported alongside the boundary
 * because this file holds components and nothing else, which is what keeps fast refresh working.
 */
export const AddressUnavailable = () => {
    const { t } = useTranslation();

    return (
        <div data-testid="document-address-section">
            <Alert
                type="error"
                showIcon
                data-testid="address-render-failed"
                message={t('documentScan.address.renderFailed')}
                description={t('documentScan.address.renderFailedDetail')}
            />
        </div>
    );
};
